import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { aiCandidateAPI, errMsg } from '../../../lib/aiInterviewApi';
import { distance, loadFaceKit } from '../faceKit';
import { MEDIA_ERRORS } from '../media';
import { IdCapture, SelfieCapture } from './IdentityCaptures';
import LivenessCheck from './LivenessCheck';

const MATCH_THRESHOLD = 0.55;
const SUB = [['id', 'ID photo'], ['selfie', 'Selfie'], ['liveness', 'Liveness'], ['review', 'Confirm']];

const Thumb = ({ blob, label, testId }) => {
  // Create + revoke inside one effect so React StrictMode's double-mount doesn't leave a revoked URL.
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!blob) return undefined;
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return <figure>{url && <img src={url} alt={label} data-testid={testId} />}<figcaption>{label}</figcaption></figure>;
};

function Review({ idShot, selfie, live, onRetake, onSubmit, busy }) {
  const dist = distance(idShot.descriptor, selfie.descriptor);
  const matched = dist !== null && dist <= MATCH_THRESHOLD;
  const allGood = matched && live.passed && !!idShot.faces;
  return (
    <div data-testid="identity-review">
      <div className="iv-thumbs">
        <Thumb blob={idShot.blob} label="ID photo" testId="identity-review-id" />
        <Thumb blob={selfie.blob} label="Selfie" testId="identity-review-selfie" />
      </div>
      <div className="iv-status-list">
        <div className="iv-status" data-testid="identity-match-status">
          <span>Face match</span>
          <span className={`iv-pill iv-tone-${matched ? 'green' : 'amber'}`}>
            {matched ? 'Match confirmed' : dist === null ? 'Not checked' : 'Not confirmed'}
            {dist !== null && <span className="iv-mono" style={{ opacity: 0.75 }}>· distance {dist.toFixed(2)} / {MATCH_THRESHOLD}</span>}
          </span>
        </div>
        <div className="iv-status" data-testid="identity-liveness-status">
          <span>Liveness</span>
          <span className={`iv-pill iv-tone-${live.passed ? 'green' : 'amber'}`}>{live.passed ? 'Confirmed' : 'Not confirmed'}</span>
        </div>
      </div>
      {!allGood && (
        <div className="iv-note is-warn" style={{ marginTop: '0.75rem' }}>
          We couldn't confirm everything automatically. You can retake the photos, or continue: a person from the hiring team will review the images. Nothing is decided automatically.
        </div>
      )}
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-ghost" onClick={onRetake} data-testid="identity-retake-btn">Retake</button>
        <button type="button" className="iv-btn iv-btn-cta" disabled={busy} onClick={() => onSubmit(dist, !allGood)} data-testid="candidate-id-verify-complete-btn">
          {busy ? 'Submitting…' : allGood ? 'Enter AI Interview Room' : 'Continue — a person will review'}
        </button>
      </div>
    </div>
  );
}

export default function IdentityStep({ token, media, onDone }) {
  const [kit, setKit] = useState('loading');
  const [stage, setStage] = useState('id');
  const [idShot, setIdShot] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    media.ensure().catch(() => {});
    loadFaceKit().then(() => setKit('ready')).catch(() => setKit('failed'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (dist, final) => {
    setBusy(true);
    const fd = new FormData();
    fd.append('id_photo', idShot.blob, 'id.jpg');
    fd.append('selfie', selfie.blob, 'selfie.jpg');
    fd.append('meta', JSON.stringify({
      match_distance: dist, liveness: live, id_face_found: !!idShot.faces, selfie_faces: selfie.faces,
      descriptor: selfie.descriptor, final,
    }));
    try {
      const { data } = await aiCandidateAPI.identity(token, fd);
      toast.success(data.result === 'pass' ? 'Identity confirmed' : 'Submitted for human review');
      onDone();
    } catch (e) {
      if (e.response?.status === 429) { toast.message('ID check already recorded'); onDone(); return; }
      toast.error(errMsg(e, 'Could not submit the ID check'));
    } finally {
      setBusy(false);
    }
  };

  const ready = kit === 'ready';
  const idx = SUB.findIndex(([k]) => k === stage);
  return (
    <section className="iv-card" data-testid="candidate-identity-card">
      <h1 className="iv-h1">ID & liveness check</h1>
      <p className="iv-sub" style={{ marginBottom: '0.75rem' }}>
        This runs in your browser. Your ID photo and selfie are stored separately with stricter access and deleted under the retention policy.
      </p>
      <div className="iv-substeps">
        {SUB.map(([k, l], i) => <span key={k} className={`iv-step ${i < idx ? 'is-done' : i === idx ? 'is-current' : ''}`}>{l}</span>)}
      </div>
      {kit === 'loading' && <div className="iv-note" style={{ marginBottom: '0.75rem' }} data-testid="identity-kit-loading">Loading the face-check models…</div>}
      {kit === 'failed' && <div className="iv-note is-warn" style={{ marginBottom: '0.75rem' }}>The automatic face check isn't available on this device. You can still continue; a person will review.</div>}
      {media.error && <div className="iv-note is-bad">{MEDIA_ERRORS[media.error]}</div>}
      {!media.error && kit !== 'loading' && (
        <>
          {stage === 'id' && <IdCapture stream={media.stream} kitReady={ready} onCaptured={(s) => { setIdShot(s); setStage('selfie'); }} />}
          {stage === 'selfie' && <SelfieCapture stream={media.stream} kitReady={ready} onCaptured={(s) => { setSelfie(s); setStage('liveness'); }} />}
          {stage === 'liveness' && <LivenessCheck stream={media.stream} kitReady={ready} selfieDescriptor={selfie?.descriptor} onDone={(r) => { setLive(r); setStage('review'); }} />}
          {stage === 'review' && <Review idShot={idShot} selfie={selfie} live={live} busy={busy} onRetake={() => setStage('id')} onSubmit={submit} />}
        </>
      )}
    </section>
  );
}
